import {LuiV3ColorKey} from '@app/lui3/constants';
import {
  ComponentProps,
  forwardRef,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useState,
  useTransition,
} from 'react';

import useChangeImmediate from '../hooks/useChangeImmediate';
import Icon, {IconType} from '../Icon';
import {ColorName} from '../Theme/colors';
import BtnContainer from './BtnContainer';
import {
  ButtonColorPalette,
  LegacyVariantName,
  useConcreteColorsFromVariant,
  useLegacyVariantColors,
} from './hooks';
import FilledBaseLayer from './layers/FilledBaseLayer';
import FilledContentLayer from './layers/FilledContentLayer';
import FilledDisabledBaseLayer from './layers/FilledDisabledBaseLayer';
import LoadingStateLayer from './layers/LoadingStateLayer';
import OutlineBaseLayer from './layers/OutlineBaseLayer';
import OutlineContentLayer from './layers/OutlineContentLayer';
import OutlineDisabledBaseLayer from './layers/OutlineDisabledBaseLayer';
import TextButtonContentLayer from './layers/TextButtonContentLayer';
import TextButtonInteractionLayer from './layers/TextButtonInteractionLayer';
import TextContentLayer from './layers/TextContentLayer';
import TextInheritContentLayer from './layers/TextInheritContentLayer';
import TextInheritInteractionLayer from './layers/TextInheritInteractionLayer';
import TextInteractionLayer from './layers/TextInteractionLayer';
import AffixContainer from './layout/AffixContainer';
import ChildrenContainer from './layout/ChildrenContainer';
import InternalLayout from './layout/InternalLayout';

interface Props extends ComponentProps<typeof BtnContainer> {
  variant?: Variant | LegacyVariantName;
  color?: ColorName;
  compact?: boolean;
  size?: Size;
  fill?: boolean;
  /**
   * @alias beforeIcon
   */
  icon?: Props['beforeIcon'];
  beforeIcon?: IconType | ReactElement;
  before?: ReactNode;
  afterIcon?: IconType | ReactElement;
  after?: ReactNode;
  round?: boolean;
  loading?: boolean;
  skipLoaded?: boolean;
  error?: boolean;
  immediate?: boolean;
  children?: ReactNode;
  square?: boolean;
  underline?: boolean;
  overrideColorPalette?: ButtonColorPalette;
  /**
   * @deprecated use {@link loading} and {@link error}
   */
  state?: keyof typeof LEGACY_STATE_TO_LOADING_STATE;
}

export type Variant =
  | 'filled'
  | 'filled_tonal'
  | 'outline'
  | 'text'
  | 'text_inherit'
  | 'text_button';

type Size = keyof typeof SIZE;

interface BaseProps {
  disabled: boolean;
  backgroundColor: LuiV3ColorKey;
  hoverColor: LuiV3ColorKey;
  outlineColor: LuiV3ColorKey;
  hoverOutlineColor: LuiV3ColorKey;
}

const FilledBase = ({disabled, backgroundColor, hoverColor}: BaseProps) =>
  disabled ? (
    <FilledDisabledBaseLayer backgroundColor={backgroundColor} />
  ) : (
    <>
      <FilledBaseLayer
        backgroundColor={backgroundColor}
        hoverColor={hoverColor}
      />
    </>
  );

const OutlineBase = ({
  disabled,
  backgroundColor,
  hoverColor,
  outlineColor,
  hoverOutlineColor,
}: BaseProps) =>
  disabled ? (
    <OutlineDisabledBaseLayer outlineColor={outlineColor} />
  ) : (
    <>
      <OutlineBaseLayer
        backgroundColor={backgroundColor}
        outlineColor={outlineColor}
        hoverColor={hoverColor}
        hoverOutlineColor={hoverOutlineColor}
      />
    </>
  );

const TextBase = ({disabled, backgroundColor, hoverColor}: BaseProps) =>
  disabled ? null : (
    <TextInteractionLayer
      backgroundColor={backgroundColor}
      hoverColor={hoverColor}
    />
  );

const TextInheritBase = ({disabled}: BaseProps) =>
  disabled ? null : <TextInheritInteractionLayer />;

const TextButtonBase = ({disabled, backgroundColor, hoverColor}: BaseProps) =>
  disabled ? null : (
    <TextButtonInteractionLayer
      backgroundColor={backgroundColor}
      hoverColor={hoverColor}
    />
  );

const BASE = {
  filled: FilledBase,
  filled_tonal: FilledBase,
  outline: OutlineBase,
  text: TextBase,
  text_inherit: TextInheritBase,
  text_button: TextButtonBase,
} as const;

const CONTENT = {
  filled: FilledContentLayer,
  filled_tonal: FilledContentLayer,
  outline: OutlineContentLayer,
  text: TextContentLayer,
  text_inherit: TextInheritContentLayer,
  text_button: TextButtonContentLayer,
} as const;

export const SIZE = {
  default: undefined,
  huge: '4rem',
  big: '3.125rem',
  compact: '2.125rem',
  medium: '1.875rem',
  small: '1.5rem',
  tiny: '1.25rem',
} as const;

const LEGACY_STATE_TO_LOADING_STATE = {
  unsaved: undefined,
  completed: 'loaded',
  error: 'error',
  loading: 'loading',
} as const;

const Btn = forwardRef<HTMLElement, Props>(
  (
    {
      variant: rawVariant = 'outline',
      color: rawColor = 'secondary',
      disabled = false,
      children,
      before,
      icon,
      beforeIcon: rawBeforeIcon,
      after,
      afterIcon,
      loading = false,
      skipLoaded = false,
      error,
      state: rawState,
      onClick: rawOnClick,
      compact,
      size = compact ? 'compact' : 'default',
      round,
      square,
      immediate,
      underline,
      overrideColorPalette,
      ...rest
    },
    ref
  ) => {
    const [transitioning, startTransition] = useTransition();

    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
      if (!loaded) {
        return;
      }
      const timeout = setTimeout(() => setLoaded(false), 1000);
      return () => clearTimeout(timeout);
    }, [loaded]);

    useChangeImmediate(() => {
      if (!loading) {
        setLoaded(true);
      }
    }, [loading]);

    // this is to support alternative(/legacy?) button configurations
    const {variant, color} = useLegacyVariantColors(rawVariant, rawColor);
    const colorPaletteFromVariant = useConcreteColorsFromVariant(
      variant,
      color
    );
    const {
      backgroundColor,
      contentColor,
      hoverColor,
      outlineColor,
      hoverOutlineColor,
    } = {...colorPaletteFromVariant, ...overrideColorPalette};

    const Base = BASE[variant];
    const Content = CONTENT[variant];

    const state: ComponentProps<typeof LoadingStateLayer>['state'] =
      (loading && 'loading') ||
      (loaded && (error ? 'error' : !skipLoaded ? 'loaded' : undefined)) ||
      (rawState && LEGACY_STATE_TO_LOADING_STATE[rawState]) ||
      (transitioning && 'loading') ||
      undefined;

    const beforeIcon = rawBeforeIcon || icon;

    const onClick = useCallback<NonNullable<typeof rawOnClick>>(
      (...args) => rawOnClick && startTransition(() => rawOnClick(...args)),
      [rawOnClick]
    );

    return (
      <BtnContainer
        {...rest}
        size={SIZE[size]}
        fontSize={size === 'big' ? '1rem' : undefined}
        disabled={disabled || state === 'loading'}
        onClick={immediate ? rawOnClick : onClick}
        round={round}
        square={square}
        ref={ref}
        data-selected={
          variant === 'outline' && color === 'primary' ? true : undefined
        }
      >
        <Base
          disabled={disabled}
          backgroundColor={backgroundColor as LuiV3ColorKey}
          hoverColor={hoverColor as LuiV3ColorKey}
          hoverOutlineColor={hoverOutlineColor as LuiV3ColorKey}
          outlineColor={outlineColor as LuiV3ColorKey}
        />
        <Content
          disabled={disabled}
          contentColor={contentColor as LuiV3ColorKey}
        >
          <InternalLayout style={state && {opacity: 0}}>
            {beforeIcon && (
              <AffixContainer>
                {typeof beforeIcon === 'string' ? (
                  <Icon icon={beforeIcon} />
                ) : (
                  beforeIcon
                )}
              </AffixContainer>
            )}
            {before && <AffixContainer>{before}</AffixContainer>}
            {children && (
              <ChildrenContainer underline={underline}>
                {children}
              </ChildrenContainer>
            )}
            {after && <AffixContainer>{after}</AffixContainer>}
            {afterIcon && (
              <AffixContainer>
                {typeof afterIcon === 'string' ? (
                  <Icon icon={afterIcon} />
                ) : (
                  afterIcon
                )}
              </AffixContainer>
            )}
          </InternalLayout>
          <LoadingStateLayer state={state} />
        </Content>
      </BtnContainer>
    );
  }
);

export default Btn;
